import { APPS, AppDefinition } from "@/constants/appNavigation";
import { canAccessAsDelegate } from "@/constants/delegationPermissions";
import { DelegatedRouteUserInfo } from "@/lib/delegationRouteAccess";

export const getCurrentApp = (pathname: string): AppDefinition =>
  APPS.find((app) => app.match(pathname)) ?? APPS[0];

export const isSelskabsblankettenRoute = (pathname: string): boolean =>
  pathname.startsWith("/selskabsblanketten");

type AppNavigationUserInfo = DelegatedRouteUserInfo & {
  identityType?: "Personal" | "Company" | "Accountant";
};

/**
 * En firma- eller revisor-session har ikke et person-id: `userInfo.id` er dér et Firma- eller
 * Revisor-id. Efteruddannelse er en personlig forpligtelse, og CLE-API'et udleder ejeren af
 * sessionen — så alt uploadet i en sådan session fik firmaets id som ejer, kunne ikke slås op
 * som person i backoffice, og kunne aldrig indberettes. API'et afviser det nu; her sørger vi
 * for, at brugeren ikke bliver tilbudt en funktion, der kun kan ende i en afvisning.
 *
 * Fail-open når feltet mangler, og det er med vilje: værnet ligger i API'et. Svarede en ældre
 * backend uden `identityType`, ville et skjul her spærre rigtige advokater ude af deres egen
 * efteruddannelse — en dyrere fejl end at vise et menupunkt, API'et alligevel afviser.
 */
const hidesPersonalApps = (userInfo: AppNavigationUserInfo): boolean =>
  userInfo.identityType !== undefined && userInfo.identityType !== "Personal";

export const getVisibleApps = (
  userInfo: AppNavigationUserInfo | null | undefined,
): AppDefinition[] => {
  if (!userInfo) {
    return APPS;
  }

  const visible = hidesPersonalApps(userInfo)
    ? APPS.filter((app) => !app.requiresPersonalIdentity)
    : APPS;

  if (!userInfo.isDelegatedSession) {
    return visible;
  }

  return visible.filter(
    (app) => !app.permission || canAccessAsDelegate(userInfo, app.permission),
  );
};

/**
 * Om én bestemt app må vises for denne session.
 *
 * Findes, saa forsidens LinkCards og app-skifteren afgør det paa samme regel. Kortene var
 * gated paa `canAccessAsDelegate` alene, som returnerer true for enhver ikke-delegeret
 * session — ogsaa en firma-session. Kortet til efteruddannelse blev derfor staaende, efter at
 * app-skifteren og ruten var lukket for firmaer.
 */
export const isAppVisible = (
  userInfo: AppNavigationUserInfo | null | undefined,
  appId: string,
): boolean => getVisibleApps(userInfo).some((app) => app.id === appId);
